import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IBase, IBookingCreate } from 'src/app/models';
import { ExpeditionStatus, ExpeditionType, HttpResponseType } from 'src/app/models/enums';
import { objectToSnake } from 'ts-case-convert';
import { ApiRouterService } from '../http/api.router';
import { BaseHttpService } from '../http/base.http.service';
import { BookingApiRoutes } from './booking-api.routes';
import { IBookingUpdate } from 'src/app/models/booking/bookingUpdate';

@Injectable({
  providedIn: 'root',
})
export class BookingsHttpService extends BaseHttpService {
  private baseURL = `bookings`;

  constructor(
    @Inject(HttpClient) httpClient: HttpClient,
    private routerService: ApiRouterService,
  ) {
    super(httpClient);
  }

  public getBookings(size: number, page?: number) {
    let path = page ? `?page=${page}` : '';
    path +=
      path === '' ? (size ? `?size=${size}` : '') : size ? `&size=${size}` : '';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getBookingsFiltered(
    size: number,
    page: number,
    expeditionClients: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    origin: string,
    destination: string,
    cancelled?: boolean,
    faults?: boolean,
    services?: IBase[],
    routes?: IBase[],
    searchValue?: string
  ) {
    let path = `/filter?page=${page}&size=${size}`;
    if (expeditionClients.length > 0) {
      const clientIds = expeditionClients.map(client => client.id).join(',');
      path += `&clients=${clientIds}`;
    }
    if (expeditionTowns.length > 0) {
      const townsIds = expeditionTowns.map(town => town.id).join(',');
      path += `&towns=${townsIds}`;
    }
    if (services && services.length > 0) {
      const serviceIds = services.map(service => service.id).join(',');
      path += `&services=${serviceIds}`;
    }
    if (routes && routes.length > 0) {
      const routeIds = routes.map(route => route.id).join(',');
      path += `&routes=${routeIds}`;
    }
    if (expeditionTypes.length > 0) {
      const types = expeditionTypes.map(types => types).join(',');
      path += `&types=${types}`;
    }
    if (expeditionStatus.length > 0) {
      const status = expeditionStatus.map(status => status).join(',');
      path += `&status=${status}`;
    }
    if (cancelled) {
      path += `&cancelled=${true}`;
    }
    if (faults) {
      path += `&faults=${true}`;
    }
    if (origin && origin !== '') {
      path += `&origin=${origin}`
    }
    if (destination && destination !== '') {
      path += `&destination=${destination}`
    }
    path +=
      searchValue
        ? `&search=${encodeURIComponent(searchValue)}`
        : '';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getBookingsByUser(
    userId: number,
    size: number,
    page: number,
    expeditionClients: IBase[],
    expeditionTowns: IBase[],
    expeditionTypes: ExpeditionType[],
    expeditionStatus: ExpeditionStatus[],
    origin: string,
    destination: string,
    cancelled?: boolean,
    faults?: boolean,
    services?: IBase[],
    routes?: IBase[],
    searchValue?: string
  ) {
    let path = `/user/${userId}/filter?page=${page}&size=${size}`;
    if (expeditionClients.length > 0) {
      const clientIds = expeditionClients.map(client => client.id).join(',');
      path += `&clients=${clientIds}`;
    }
    if (expeditionTowns.length > 0) {
      const townsIds = expeditionTowns.map(town => town.id).join(',');
      path += `&towns=${townsIds}`;
    }
    if (services && services.length > 0) {
      const serviceIds = services.map(service => service.id).join(',');
      path += `&services=${serviceIds}`;
    }
    if (routes && routes.length > 0) {
      const routeIds = routes.map(route => route.id).join(',');
      path += `&routes=${routeIds}`;
    }
    if (expeditionTypes.length > 0) {
      const types = expeditionTypes.map(types => types).join(',');
      path += `&types=${types}`;
    }
    if (expeditionStatus.length > 0) {
      const status = expeditionStatus.map(status => status).join(',');
      path += `&status=${status}`;
    }
    if (cancelled) {
      path += `&cancelled=${true}`;
    }
    if (faults) {
      path += `&faults=${true}`;
    }
    if (origin && origin !== '') {
      path += `&origin=${origin}`
    }
    if (destination && destination !== '') {
      path += `&destination=${destination}`
    }
    path +=
      searchValue
        ? `&search=${encodeURIComponent(searchValue)}`
        : '';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getBooking(bookingId: number, cancelled?: boolean) {
    let path = `/${bookingId}`;
    if (cancelled) {
      path += `?cancelled=${true}`;
    }
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public validatePickUp(bookingId: number) {
    const path = `/${bookingId}/pick-up-time`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.updatePatch({}, url);
  }

  public validateDropOff(bookingId: number) {
    const path = `/${bookingId}/drop-off-time`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.updatePatch({}, url);
  }

  public createBooking(createBookingRequest: IBookingCreate) {
    const body = objectToSnake(
      { 
        availabilityId: createBookingRequest.availabilityId,
        targetUserId: createBookingRequest.targetUserId,
        serviceAvailabilityResponseId: createBookingRequest.serviceAvailabilityResponseId,
        serviceId: createBookingRequest.serviceId,
        originStopId: createBookingRequest.originStopId,
        destinationStops: createBookingRequest.destinationStops,
        tripId: createBookingRequest.tripId,
      }
    );
    const url = this.routerService.mount(`${this.baseURL}`);
    return this.create(body, url);
  }

  public multipleBooking(createBookingsRequest: IBookingCreate[], journey: boolean) {
    const body = createBookingsRequest.map((booking) => {
      return objectToSnake({
        availabilityId: booking.availabilityId,
        targetUserId: booking.targetUserId,
        serviceAvailabilityResponseId: booking.serviceAvailabilityResponseId,
        serviceId: booking.serviceId,
        inStop: booking.originStop,
        seats: booking.destinationStops,
        tripId: booking.tripId,
        sendNotification: booking.sendNotification
      })
    });
    const path = journey ? `/${BookingApiRoutes.JOURNEY}` : `/${BookingApiRoutes.MULTIPLE}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.create(body, url);
  }

  public updateBooking(updateBookingRequest: IBookingUpdate) {
    const body = objectToSnake(
      {
        availabilityId: updateBookingRequest.availabilityId,
        targetUserId: updateBookingRequest.targetUserId,
        loggedUserId: updateBookingRequest.loggedUserId,
        bookingId: updateBookingRequest.bookingId,
        tripId: updateBookingRequest.tripId,
        serviceId: updateBookingRequest.serviceId,
        originStop: updateBookingRequest.originStop,
        destinationStop: updateBookingRequest.destinationStop,
        seats: updateBookingRequest.seats,
        prmSeats: updateBookingRequest.prmSeats,
        bookingSeats: updateBookingRequest.bookingSeats,
        sendNotification: updateBookingRequest.sendNotification,
        recomputePrice: updateBookingRequest.recomputePrice
      }
    );
    const url = this.routerService.mount(`${this.baseURL}`);
    return this.update(body, url);
  }

  public deleteBooking(bookingId: number, comment: string, sendNotification?: boolean, refund?: boolean, fullRefund?: boolean, related?: boolean) {
    const path = `/${bookingId}`;
    let url = this.routerService.mount(`${this.baseURL}${path}`);
    const body = objectToSnake(
      { 
        id: bookingId,
        comment: comment,
        sendNotification: sendNotification,
        refund: refund,
        fullRefund: fullRefund,
        cancelRelated: related
      }
    )
    return this.delete(url, body);
  }

  public deleteMultipleBookings(bookingsToCancel: any[]) {
    const path = `/multiple`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    const body = bookingsToCancel.map(booking => (
      objectToSnake({
        id: booking.id,
        comment: booking.comment,
        sendNotification: booking.sendNotification,
        refund: booking.refund,
        fullRefund: booking.fullRefund,
        cancelRelated: booking.related
      })
    ));
    return this.delete(url, body);
  }

  public updateMissed(bookingId: number) {
    const path = `/${bookingId}/${BookingApiRoutes.MISSED}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.updatePatch({}, url);
  }

  public updateMissedSeat(bookingId: number, seatId: number) {
    const path = `/${bookingId}/${BookingApiRoutes.MISSED}/${seatId}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.updatePatch({}, url);
  }

  public isExceedingKmLimit(userId: number, inStopId: number, outStopId: number, serviceId: number, nBookings: number) {
    const userIdParam = `userId=${userId}`;
    const inStopIdParam = `inStopId=${inStopId}`;
    const outStopIdParam = `outStopId=${outStopId}`;
    const serviceIdParam = `serviceId=${serviceId}`;
    const nBookingsParam = `nBookings=${nBookings}`;
    const path = `/exceeds-km-limit?${inStopIdParam}&${outStopIdParam}&${serviceIdParam}&${userIdParam}&${nBookingsParam}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }
  
  public updateKms(bookingId: number, kms: number) {
    let path = `/${bookingId}/${BookingApiRoutes.KMS}`;
    path +=
      kms
        ? `?kms=${kms}`
        : '?kms=0';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.updatePatch({}, url);
  }

  public updateTicketStatus(bookingId: number, paid?: boolean, validated?: boolean) {
    const path = `/${bookingId}/${BookingApiRoutes.TICKET_STATUS}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    const body: { paid?: boolean; validated?: boolean } = {};
    if (paid !== undefined) body.paid = paid;
    if (validated !== undefined) body.validated = validated;
    return this.updatePatch(body, url);
  }

  public updateNotified(bookingId: number, isNotified: boolean) {
    const path = `/${bookingId}/${BookingApiRoutes.NOTIFIED}?notified=`;
    const url = this.routerService.mount(`${this.baseURL}${path}${isNotified}`);
    return this.updatePatch({}, url);
  }

  public reassignAvailability(bookingIds: number[], newAvailabilityId: number, sendNotification: boolean, recomputePrice?: boolean) {
    const body = objectToSnake(
      {
        bookings: bookingIds,
        targetAvailability: newAvailabilityId,
        sendNotification: sendNotification,
        recomputePrice: recomputePrice
      }
    );
    const path = `/trip-reassigns/async`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.update(body, url, HttpResponseType.TEXT);
  }

  public resendMail(bookingId: number, related: boolean) {
    let path = `/${bookingId}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.create({ related }, url);
  }
}
